import { Box } from "@mui/material";
import { CustomCellRendererProps } from "ag-grid-react";
import { Typography } from "@platform/design-system";

import { ServiceWaitingPeriod } from "../../../../types/rfp.api.types";
import { ClickToSelect } from "../ClickToSelect";
import { SERVICE_TYPE_LABELS } from "../constants";

interface ServiceWaitingPeriodsRendererProps extends CustomCellRendererProps {
  value: ServiceWaitingPeriod[] | number | null | undefined;
  cellEditorParams?: {
    displayMode?: string;
  };
  /**
   * Set by the SBS comparison grid. An empty cell there shows the same no-data
   * indicator as every other cell rather than prompting an edit.
   */
  comparisonView?: boolean;
}

/**
 * Cell renderer for Service Waiting Periods.
 * Displays a formatted list of selected services with their waiting periods.
 * Example:
 * "Basic: 6mo
 * Major: 12mo"
 */
export const ServiceWaitingPeriodsRenderer: React.FC<
  ServiceWaitingPeriodsRendererProps
> = (props) => {
  const value = props.value;
  const displayMonths = props.cellEditorParams?.displayMode === "months";

  const displayValueOnEmpty =
    displayMonths || props.comparisonView ? "-" : <ClickToSelect />;
  // Handle empty or invalid values
  if (!value) {
    return displayValueOnEmpty;
  } else if (!Array.isArray(value)) {
    return displayValueOnEmpty;
  } else if (value.length === 0) {
    return displayMonths ? displayValueOnEmpty : "None";
  }

  const formatted = value
    .map((period: ServiceWaitingPeriod) =>
      displayMonths
        ? period.months
        : `${SERVICE_TYPE_LABELS[period.type] || period.type} - ${period.months} month(s)`,
    )
    .join("\n");

  const tooltipText = value
    .map((period: ServiceWaitingPeriod) => {
      const serviceLabel = SERVICE_TYPE_LABELS[period.type] || period.type;
      return `${serviceLabel}: ${period.months}`;
    })
    .join("\n");

  return (
    <Box sx={{ cursor: "pointer", py: 0.5 }}>
      <Typography
        title={tooltipText}
        variant="body-medium"
        sx={{ wordBreak: "break-word", whiteSpace: "pre-line" }}
      >
        {formatted}
      </Typography>
    </Box>
  );
};

ServiceWaitingPeriodsRenderer.displayName = "ServiceWaitingPeriodsRenderer";
